import { format, isToday } from 'date-fns'
import { isPossiblePhoneNumber } from 'libphonenumber-js'

export function isFieldEmpty(field: string): boolean {
  return !field || field.trim().length === 0
}

export function isGmailAddress(email: string): boolean {
  const regexp = new RegExp(/@gmail.com$/)
  return regexp.test(email)
}

export function isValidWebAddressFormat(url: string): boolean {
  const regexp =
    /^(?:(?:https?|http):\/\/)?(?:(?!(?:10|127)(?:\.\d{1,3}){3})(?!(?:169\.254|192\.168)(?:\.\d{1,3}){2})(?!172\.(?:1[6-9]|2\d|3[0-1])(?:\.\d{1,3}){2})(?:[1-9]\d?|1\d\d|2[01]\d|22[0-3])(?:\.(?:1?\d{1,2}|2[0-4]\d|25[0-5])){2}(?:\.(?:[1-9]\d?|1\d\d|2[0-4]\d|25[0-4]))|(?:(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)(?:\.(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)*(?:\.(?:[a-z\u00a1-\uffff]{2,})))(?::\d{2,5})?(?:\/\S*)?$/

  return regexp.test(url)
}

function isValidEmailFormat(email: string): boolean {
  const regexp = new RegExp(
    /^(([^<>()ß[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
  )
  return regexp.test(email)
}

export function isPhoneNumberValid(field: string, optional: boolean = false): boolean {
  return isFieldEmpty(field) ? optional : isPossiblePhoneNumber(field, 'US')
}

export function isWebAddressValid(field: string, optional: boolean = false): boolean {
  return isFieldEmpty(field) ? optional : isValidWebAddressFormat(field)
}

export function isEmailAddressValid(field: string, optional: boolean = false): boolean {
  return isFieldEmpty(field) ? optional : isValidEmailFormat(field)
}

export function isValidLinkedInUrl(url: string) {
  const pattern = /^https?:\/\/(?:www\.)?linkedin\.com\/(?:in|pub|profile)\/[a-zA-Z0-9-_]+\/?$/
  return pattern.test(url)
}

export function isValidTwitterUrl(url: string) {
  const pattern = /^https?:\/\/(?:www\.)?twitter\.com\/[a-zA-Z0-9_]{1,15}(\/?)$/
  return pattern.test(url)
}

const currencyMap = new Map<string, string>([
  ['$', 'USD'],
  ['USD', 'USD'],
])

export function progressValue(value?: number) {
  return value
    ? (value / 100).toLocaleString(undefined, {
        style: 'percent',
        maximumFractionDigits: 2,
      })
    : 0 + '%'
}

export function formatMonetaryValue(
  value: number,
  currency: string = 'USD',
  condenseLargeValues: boolean = true
): string {
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
  })

  // in the case of a large number over a billion dollars, we want to condense the value to be suffixed with B
  if (value >= 1000000000 && condenseLargeValues) {
    let condensedValue = value / 1000000000
    return '$' + roundToTwoDecimals(condensedValue) + 'B'
  }

  // in the case of a large number over a million dollars, we want to condense the value to be suffixed with M
  if (value >= 1000000 && condenseLargeValues) {
    let condensedValue = value / 1000000
    return '$' + roundToTwoDecimals(condensedValue) + 'M'
  }

  // in the case of a number over a thousand dollars, we want to condense the value to be suffixed with a K
  if (value >= 1000 && condenseLargeValues) {
    let condensedValue = value / 1000
    return '$' + roundToTwoDecimals(condensedValue) + 'K'
  }

  return formatter.format(value)
}

export interface FormatKpiOptions {
  hideUnit?: boolean
}

export function formatKpiValue(value: number, unit?: string, options?: FormatKpiOptions): string {
  const key = unit && currencyMap.get(unit.trim().toLocaleUpperCase())

  if (key) {
    return formatMonetaryValue(value, key, unit === '$')
  }

  if (!unit || options?.hideUnit) {
    return value.toLocaleString()
  }

  return value.toLocaleString() + (unit.trim() === '%' ? unit : ' ' + unit)
}

export function formatUnit(unit?: string) {
  if (!unit) {
    return ''
  }

  return unit.trim() === '%' ? unit : ' ' + unit
}

export interface MaybePluralizeOptions {
  showZeros?: boolean
  hideCount?: boolean
}

export function maybePluralize(count: number, singular: string, plural: string, options?: MaybePluralizeOptions) {
  if (!count && !options?.showZeros) {
    return plural
  }

  if (options?.hideCount) {
    return `${count === 1 ? singular : plural}`
  }

  return `${count} ${count === 1 ? singular : plural}`
}

// This round limits to 2 decimal places only if necessary, unlike toFixed(2)
export function roundToTwoDecimals(value: number) {
  return Math.round((value + Number.EPSILON) * 100) / 100
}

export function capitalizeFirstLetter(text: string) {
  if (text.length > 1) {
    return text.charAt(0).toUpperCase() + text.slice(1)
  }
  return text.charAt(0).toUpperCase()
}

export function capitalizeOnlyFirstLetter(text: string) {
  if (text.length > 1) {
    return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase()
  }
  return text.charAt(0).toUpperCase()
}

export function checkForDuplicates(array: string[]) {
  return new Set(array).size !== array.length
}

/**
 * Takes a string phrase and breaks it into separate phrases
 * no bigger than 'maxwidth', breaks are made at complete words.
 */
export function formatLabel(str: string, maxwidth: number) {
  const sections: string[] = []
  const words = str.split(' ')
  let temp = ''

  words.forEach(function (item, index) {
    if (temp.length > 0) {
      var concat = temp + ' ' + item

      if (concat.length > maxwidth) {
        sections.push(temp)
        temp = ''
      } else {
        if (index === words.length - 1) {
          sections.push(concat)
          return
        } else {
          temp = concat
          return
        }
      }
    }

    if (index === words.length - 1) {
      sections.push(item)
      return
    }

    if (item.length < maxwidth) {
      temp = item
    } else {
      sections.push(item)
    }
  })

  return sections
}

// returns a string that shows "X units ago" where X is the number of units and units is the unit of time
export function timeAgo(dateString: string) {
  const inputDate = new Date(dateString)
  const now = new Date()

  const diffInSeconds: number = Math.floor((now.valueOf() - inputDate.valueOf()) / 1000)
  const minute = 60
  const hour = minute * 60
  const day = hour * 24
  const month = day * 30
  const year = day * 365

  if (diffInSeconds < minute) {
    return `(${diffInSeconds} seconds ago)`
  } else if (diffInSeconds < hour) {
    return `(${Math.floor(diffInSeconds / minute)} minutes ago)`
  } else if (diffInSeconds < day) {
    return `(${Math.floor(diffInSeconds / hour)} hours ago)`
  } else if (diffInSeconds < month) {
    return `(${Math.floor(diffInSeconds / day)} days ago)`
  } else if (diffInSeconds < year) {
    return `(${Math.floor(diffInSeconds / month)} months ago)`
  } else {
    return `(${Math.floor(diffInSeconds / year)} years ago)`
  }
}

// gives back time as 12:00 AM/PM
export function formatDateAsTime(date: Date) {
  return format(date, 'h:mm a') + ' ' + timeAgo(date.toISOString())
}

// gives back day either as 'today' or 'MMM dd'
export function formatDate(date: Date) {
  if (isToday(date)) {
    return 'today'
  } else {
    return format(date, 'MMM d')
  }
}

// gives back day as the full month, day, and year
export function formatMonthYear(date: Date) {
  return format(date, 'MMMM d, yyyy')
}

// gives back the quarter and year for a given date
export function periodAndYear(date: Date) {
  let period = date.getMonth() + 1
  period = Math.ceil(period / 3)

  const year = date.getFullYear()

  return {
    period: `q${period}`,
    year,
  }
}

// used in conjunction with filter(notEmpty) to return non empty values in an array
export function notEmpty<TValue>(value: TValue | null | undefined): value is TValue {
  return value !== null && value !== undefined
}

// gives back range as Jan 1-8, 2023
export function dateRange(startDate: Date, endDate: Date) {
  return `${format(new Date(startDate), `MMM`)} ${format(new Date(startDate), `d`)}-${format(
    new Date(endDate),
    `d`
  )}, ${format(new Date(startDate), `yyyy`)}`
}

export function formatTimeStampWithSlashes(timestamp: string) {
  const date = new Date(timestamp)
  return format(date, 'MM/dd/yyyy')
}

export function formatTimestamp(timestamp: string) {
  const today = new Date() // Current date
  const yesterday = new Date(today) // Create a new date object with today's date
  yesterday.setDate(yesterday.getDate() - 1) // Subtract one day to get yesterday's date

  const date = new Date(timestamp)

  if (
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear()
  ) {
    return 'Today'
  } else if (
    date.getDate() === yesterday.getDate() &&
    date.getMonth() === yesterday.getMonth() &&
    date.getFullYear() === yesterday.getFullYear()
  ) {
    return 'Yesterday'
  } else {
    return format(date, 'MMM d, yyyy')
  }
}

// this function take a number and decides whether or not to put a + after it, it also turns t
export function formatNumberWithTensAndPlus(value: number) {
  // first, we want to round the number to the nearest 10
  const roundedNumber = Math.round(value / 10) * 10

  // if the number is less than or equal to 10, we should just return the number
  if (roundedNumber <= 10) {
    return value
  }

  // then, we check to see if the number is different from the rounded number, if so, we show a +
  if (roundedNumber !== value) {
    return roundedNumber + '+'
  }
  return value
}
